import { Drawer, Box, Typography, IconButton, Chip, Divider, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { FormularioVisita } from './FormularioVisita';
import type { Visita } from '../hooks/useVisitas';

// AIDEV-NOTE: Mapeamento de status da visita para cor do Chip MUI
const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  em_andamento: 'info',
  concluida: 'success',
  cancelada: 'error'
};

const STATUS_LABEL: Record<string, string> = {
  em_andamento: 'Em andamento',
  concluida: 'Concluida',
  cancelada: 'Cancelada'
};

interface VisitaDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  visita: Visita | null;
}

function formatDuration(minutes?: number): string {
  if (!minutes) return '--';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

function formatCoord(lat?: number, lng?: number): string {
  if (lat == null || lng == null) return 'Nao disponivel';
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

export function VisitaDetailDrawer({ open, onClose, visita }: VisitaDetailDrawerProps) {
  if (!visita) return null;

  const isReadOnly = visita.status === 'concluida' || visita.status === 'cancelada';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 400, maxWidth: '100vw' } }}
    >
      <Box p={2} display="flex" flexDirection="column" height="100%">
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" noWrap>
              {visita.cliente_nome ?? `Cliente ${visita.cliente_id}`}
            </Typography>
            <Chip
              label={STATUS_LABEL[visita.status] ?? visita.status}
              size="small"
              color={STATUS_COLOR[visita.status] ?? 'default'}
              sx={{ mt: 0.5 }}
            />
          </Box>
          <IconButton onClick={onClose} aria-label="fechar">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Secao GPS */}
        <Box my={2}>
          <Typography variant="subtitle2" gutterBottom>
            <LocationOnIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            Localizacao
          </Typography>
          <Stack spacing={1} ml={1}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Check-in
              </Typography>
              <Typography variant="body2">
                {formatCoord(visita.checkin_lat, visita.checkin_lng)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(visita.checkin_at).toLocaleString('pt-BR')}
              </Typography>
            </Box>
            {visita.checkout_at && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Check-out
                </Typography>
                <Typography variant="body2">
                  {formatCoord(visita.checkout_lat, visita.checkout_lng)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(visita.checkout_at).toLocaleString('pt-BR')}
                </Typography>
              </Box>
            )}
            <Box display="flex" alignItems="center" gap={0.5}>
              <AccessTimeIcon fontSize="small" color="action" />
              <Typography variant="body2">
                Duracao: {formatDuration(visita.duracao_minutos)}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider />

        {/* Secao Formulario */}
        <Box my={2} flex={1} overflow="auto">
          <Typography variant="subtitle2" gutterBottom>
            Formulario da Visita
          </Typography>
          <FormularioVisita
            visitaId={visita.id}
            initialValues={visita.formulario}
            readOnly={isReadOnly}
          />
        </Box>
      </Box>
    </Drawer>
  );
}
